import { useCallback } from 'react';
import { useUser } from '../contexts/UserContext';

/**
 * useFeatureGate — single source of truth for free-tier credit gating
 * across every feature pane. Each feature gets 5 free credits on signup,
 * tracked per-column on the server (see server.js FEATURE_TO_COLUMN).
 *
 * Subscribed users always pass through; the hook reports `isFree: false`
 * and `locked: false` so the pane renders plain buttons with no badge.
 *
 * For free users, the hook:
 *   • surfaces the current per-feature balance via `credits`,
 *   • flags `locked: true` when the balance hits zero,
 *   • exposes `deduct()` which POSTs to /api/<feature>/deduct-credit and
 *     updates the in-memory context so any badge re-renders without a
 *     round trip, and
 *   • exposes `openPaywall()` which fires the global openPricingModal
 *     event the Header already listens for.
 *
 * Usage:
 *   const gate = useFeatureGate('interviewAssistant');
 *   const onStart = async () => {
 *     if (gate.locked) { gate.openPaywall(); return; }
 *     const { ok } = await gate.deduct();
 *     if (!ok) return;
 *     // ... proceed with the actual feature work
 *   };
 *   <button disabled={gate.locked} onClick={onStart}>
 *     Start <FreeCreditsLabel feature="interviewAssistant" />
 *   </button>
 */

// Maps the camelCase keys used in UserContext.featureCredits (and shared
// with the server JSON shape from /api/user/credits) to the kebab-case
// path segment used by each /api/<feature>/deduct-credit endpoint.
// A couple don't share the same root: resumeBuilder hits /api/resume,
// portfolioBuilder hits /api/portfolio — those are intentional and
// match server.js routes.
const FEATURE_ENDPOINT = {
  interviewAssistant: 'interview-assistant',
  mockInterview:      'mock-interview',
  interviewQuestions: 'interview-questions',
  resumeBuilder:      'resume',
  coverLetter:        'cover-letter',
  atsCheck:           'ats-check',
  linkedinOptimizer:  'linkedin-optimizer',
  portfolioBuilder:   'portfolio',
  autoApply:          'auto-apply',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export function useFeatureGate(feature) {
  const {
    featureCredits,
    setFeatureCredits,
    hasActiveSubscription,
    fetchCredits,
  } = useUser();

  const credits = Number(featureCredits?.[feature] ?? 0);
  const isPro = !!hasActiveSubscription;
  const isFree = !isPro;
  const locked = isFree && credits <= 0;

  const openPaywall = useCallback(() => {
    try { document.dispatchEvent(new CustomEvent('openPricingModal')); }
    catch (_) { /* SSR / no DOM — ignore */ }
  }, []);

  const deduct = useCallback(async () => {
    // Pros bypass the API call entirely — no credits to decrement.
    if (isPro) return { ok: true, remaining: Infinity };
    if (credits <= 0) {
      openPaywall();
      return { ok: false, reason: 'no-credits' };
    }
    const path = FEATURE_ENDPOINT[feature];
    if (!path) {
      console.warn('[useFeatureGate] unknown feature key:', feature);
      return { ok: false, reason: 'unknown-feature' };
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/${path}/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        // Server says we're out — clamp the local count so the badge
        // updates and the paywall opens.
        setFeatureCredits(prev => ({ ...(prev || {}), [feature]: 0 }));
        openPaywall();
        return { ok: false, reason: 'no-credits' };
      }
      if (!res.ok) {
        return { ok: false, reason: data?.error || `server-error-${res.status}` };
      }
      if (typeof data.remainingCredits === 'number') {
        setFeatureCredits(prev => ({ ...(prev || {}), [feature]: data.remainingCredits }));
      } else {
        // Defensive fallback — server didn't echo a number, refetch.
        fetchCredits();
      }
      return { ok: true, remaining: data.remainingCredits ?? credits - 1 };
    } catch (err) {
      console.error('[useFeatureGate] deduct failed:', err);
      return { ok: false, reason: err.message };
    }
  }, [feature, credits, isPro, setFeatureCredits, openPaywall, fetchCredits]);

  return { isPro, isFree, credits, locked, deduct, openPaywall };
}
